import { useInfiniteQuery, UseInfiniteQueryResult } from "@tanstack/react-query";
import classNames from "classnames";
import { Entry } from "contentful";
import { useInView } from "framer-motion";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import React, { useEffect, useMemo, useRef, useState } from "react";

import HighlightedText from "~/components/common/highlighted-text";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useCategory } from "~/contexts/category";
import { useConfiguration } from "~/contexts/configuration";
import { usePageTracked } from "~/contexts/page-trackings";
import { useServerSideProps } from "~/contexts/server-side-props";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import evaUtils from "~/utils/eva-utils";
import TrackingUtils from "~/utils/tracking-utils";

import BreakpointAwareAsset from "../common/breakpoint-aware-asset";
import Container from "../common/container";
import EscapeNewLine from "../common/escape-new-line";
import Grid from "../common/grid";
import SkeletonProductTile from "../common/skeletons/skeleton-product-tile";
import EditorialLink from "../editorials/editorial-link/editorial-link";
import Breadcrumbs, { Breadcrumb } from "../product/product-breadcrumbs";
import ProductNavigationItemsCurrentPath from "../product/product-navigation-items-current-path";
import CategoryFilters from "./category-filters";
import EditorialElementPLP from "./editorial-element/editorial-element-plp";
import styles from "./product-grid.module.scss";
import ProductGridCustomBackground from "./product-grid-custom-background";

const ProductTile = dynamic(() => import("./product-tile"), {
  loading: () => <SkeletonProductTile />,
});

export function NumberProducts({ query }: { query: UseInfiniteQueryResult<EVA.Core.SearchProductsResponse, unknown> }) {
  const t = useTranslations();

  return (
    <>
      {!!query.data?.pages[0].Total && query.data?.pages[0].Total > 0 ? (
        <div className={styles.prodCount}>
          <p>{t("generic.n_products", { number: query.data?.pages[0].Total })}</p>
        </div>
      ) : (
        <div className={styles.noResults}>
          <h2>{t("generic.noresults")}</h2>
          <p dangerouslySetInnerHTML={{ __html: t("generic.noresultsparagraph") }} />
        </div>
      )}
    </>
  );
}

type Props = {
  breadcrumbs: Breadcrumb[];
  components?: Entry<Editorials.GridElement>[];
  navigation: Entry<unknown>;
  changeHeaderLayout?: boolean;
  backgroundColors?: string[];
  setId?: string;
  listScope?: string;
  initialData?: string;
};

export default function ProductGrid(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const { eva } = useConfiguration();
  const { sort, category } = useCategory();
  const { serverSideProps } = useServerSideProps();

  let countEditorialElement = 0;

  const query = useInfiniteQuery({
    queryKey: ["productListing", category.fields.categoryId, props.setId, sort],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: ({ pageParam = 1 }) => {
      if (props.setId) {
        return evaUtils.getProductsBySetWithChildren(eva, props.setId, sort, pageParam);
      } else {
        return evaUtils.getProductsByCategoryWithChildren(eva, category.fields.categoryId, sort, pageParam);
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.Total! > lastPage.Page! * lastPage.PageSize!) {
        return lastPage.Page! + 1;
      }

      return undefined;
    },
    enabled: !!category.fields.categoryId || props.setId !== undefined,
    initialData: () => {
      if (props.initialData) {
        // Verify that this initial data was created for this specific query key
        const initialKey = serverSideProps.initialKey;
        const currentKey = JSON.stringify(["productListing", category.fields.categoryId, props.setId, sort]);

        if (initialKey === currentKey) {
          return { pages: [JSON.parse(props.initialData)], pageParams: [1] };
        }
      }

      return undefined;
    },
  });

  const products = query.data?.pages.flatMap((page) => page.Products ?? []);

  const productsWithEditorialElement: (CrossCountry.Product | Entry<Editorials.GridElement>)[] | undefined =
    useMemo(() => {
      const productsWithEditorialElementTemp: (CrossCountry.Product | Entry<Editorials.GridElement>)[] | undefined =
        products;

      const editorialElementsWithoutDraftContent = props.components?.filter(isActiveEntry);

      const editorialElementsWithoutDuplicates = editorialElementsWithoutDraftContent?.reduce(
        (accumulator: Entry<Editorials.GridElement>[], current) => {
          if (!accumulator.find((item) => item.fields.gridIndex === current.fields.gridIndex)) {
            accumulator.push(current);
          }
          return accumulator;
        },
        []
      );

      const editorialElementsSortedByGridIndex = editorialElementsWithoutDuplicates?.sort(
        (number1, number2) => number1.fields.gridIndex - number2.fields.gridIndex
      );

      editorialElementsSortedByGridIndex?.forEach((editorialElement, idx) => {
        //editorial elements with an index higher than the current number of products will not be inserted
        if (
          productsWithEditorialElementTemp &&
          editorialElement.fields.gridIndex < productsWithEditorialElementTemp?.length - idx
        ) {
          productsWithEditorialElementTemp?.splice(editorialElement.fields.gridIndex, 0, editorialElement);
        }
      });
      return productsWithEditorialElementTemp;
    }, [products, props.components]);

  const isPageTracked = usePageTracked();
  const lastTrackedIndex = useRef(0);
  useEffect(() => {
    lastTrackedIndex.current =
      isPageTracked && products && props.listScope
        ? TrackingUtils.trackPLPImpression(products, lastTrackedIndex.current, props.listScope)
        : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageTracked, products]);

  const [showMore, setShowMore] = useState(false);

  const sentinelRef = useRef(null);

  const sentinelInView = useInView(sentinelRef, { margin: "0% 0% 50% 0%" });

  // handle infinite scroll
  useEffect(() => {
    if (sentinelInView && query.hasNextPage) {
      query.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelInView]);

  return (
    <div className={styles.pageWrapper}>
      {props.backgroundColors && !props.changeHeaderLayout && breakpoint === "desktop" ? (
        <ProductGridCustomBackground
          color1={props.backgroundColors[0]}
          color2={props.backgroundColors[1] ? props.backgroundColors[1] : props.backgroundColors[0]}
          changeHeaderLayout={props.changeHeaderLayout}
          defaultDesktopBackground
          hasBannerHeader={!!category.fields.bannerHeader}
        />
      ) : null}
      {category.fields.bannerHeader ? (
        <div className={styles.bannerHeaderWrapper}>
          <BreakpointAwareAsset entry={category.fields.bannerHeader} />
          <div className={styles.textWrapper}>
            {category.fields.previousPageLink ? <EditorialLink entry={category.fields.previousPageLink} /> : null}
            <h1>
              <HighlightedText text={category.fields?.formattedName ?? category.fields.name} />
            </h1>
            {category.fields.subtitle ? (
              <div className={styles.subtitle}>
                <EscapeNewLine text={category.fields.subtitle} />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <Container>
        <Grid className={styles.grid}>
          <div
            className={classNames(
              styles.headingSection,
              props.changeHeaderLayout ? styles.customHeadingSection : undefined
            )}
          >
            {props.backgroundColors && (props.changeHeaderLayout || breakpoint === "mobile") ? (
              <ProductGridCustomBackground
                color1={props.backgroundColors[0]}
                color2={props.backgroundColors[1] ? props.backgroundColors[1] : props.backgroundColors[0]}
                changeHeaderLayout={props.changeHeaderLayout}
                hasBannerHeader={!!category.fields.bannerHeader}
              />
            ) : null}
            <Container
              className={classNames(
                styles.headingColumn,
                props.changeHeaderLayout ? styles.customHeadingColumn : undefined
              )}
            >
              {props.breadcrumbs && props.breadcrumbs.length > 0 ? (
                <Breadcrumbs breadcrumbs={props.breadcrumbs} />
              ) : null}
              {!category.fields.bannerHeader ? (
                <>
                  {category.fields.previousPageLink ? <EditorialLink entry={category.fields.previousPageLink} /> : null}
                  <h1>
                    <HighlightedText text={category.fields?.formattedName ?? category.fields.name} />{" "}
                  </h1>
                </>
              ) : null}
              {category.fields.subtitle && !category.fields.bannerHeader ? (
                <div className={styles.subtitle}>
                  <EscapeNewLine text={category.fields.subtitle} />
                </div>
              ) : null}
              {category.fields.description || category.fields.readMoreDescription ? (
                <div>
                  {category.fields.description ? (
                    <p className={styles.description}>
                      <EscapeNewLine text={category.fields.description} />
                    </p>
                  ) : null}
                  {category.fields.readMoreDescription ? (
                    <p
                      className={
                        showMore ? classNames(styles.showMoreTextOpen, styles.showMoreText) : styles.showMoreText
                      }
                    >
                      <span className={styles.innerShowMoreText}>
                        <EscapeNewLine text={category.fields.readMoreDescription} />
                      </span>
                    </p>
                  ) : null}

                  {category.fields.readMoreDescription ? (
                    <button onClick={() => setShowMore(!showMore)} className={styles.showMoreButton}>
                      {showMore ? t("generic.read_less") : t("generic.read_more")}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <ProductNavigationItemsCurrentPath
                navigation={props.navigation}
                listLinks={category.fields.listLinks?.filter(isActiveEntry)}
                changeHeaderLayout={props.changeHeaderLayout}
              />
              {breakpoint === "desktop" ? <NumberProducts query={query} /> : null}
            </Container>
          </div>
          {breakpoint === "mobile" ? <NumberProducts query={query} /> : null}
          {productsWithEditorialElement?.map((item, idx) => {
            if (contentfulUtils.isContentful(item)) {
              assertEditorialType<Editorials.GridElement>(item, "gridElement");
              return (
                <div
                  key={item.sys.id}
                  id={`editorial-element-${++countEditorialElement}`}
                  style={{
                    gridColumn: `span ${
                      breakpoint === "mobile"
                        ? 6 * item.fields.numberColumnsMobile
                        : 3 * item.fields.numberColumnsDesktop
                    }`,
                  }}
                >
                  {<EditorialElementPLP editorialElement={item} />}
                </div>
              );
            } else {
              return (
                <div key={item.product_id} className={styles.tile}>
                  <ProductTile
                    product={item}
                    idxInList={idx}
                    listScope={props.listScope}
                    noLazy={idx < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP}
                  />
                </div>
              );
            }
          })}
          <div ref={sentinelRef} aria-hidden="true" />
        </Grid>
      </Container>
      <div className={styles.filters}>
        <CategoryFilters />
      </div>
    </div>
  );
}
